import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import { Button } from '../../platform/primitives/Button'
import { ButtonLink } from '../../platform/primitives/ButtonLink'
import { rememberReturnTo } from '../../auth/returnTo'
import { useAuth } from '../../auth/useAuth'
import { MFA_PATH } from '../../auth/RequireAuth'
import { OVERVIEW_PATH } from '../overview/overviewPaths'
import { writeHouseholdId } from '../household/useHousehold'
import { AuthCard } from './AuthCard'
import { SessionUnavailable } from './SessionUnavailable'
import './auth.css'

/**
 * `/invite/:token` — Task 9's copied link lands here. Without this screen the
 * link goes nowhere: the email the backend sends is unusable from a browser
 * (issue 305 — it 302s to `expensemonitor://invite/accept?token=…`, a scheme
 * nothing on desktop has registered), so a copy-link surfaced when an
 * invitation is created or resent is the only delivery mechanism M12 ships,
 * and this is what it opens. `GET /invitations/{token}/open` is that mobile
 * redirect and is never called here — this screen posts straight to `accept`.
 *
 * **A sibling of `App.tsx`'s `BARE` group, not a member.** `BARE` is wrapped
 * in `RedirectWhenSignedIn`, which bounces a signed-in visitor to Overview —
 * and the person opening an invite link is very often already signed in on
 * that browser, which is the ordinary case this screen has to serve rather
 * than the edge case. See `VERIFY_DEVICE_PATH`'s docblock in `App.tsx` for the
 * fuller argument; this route is the same shape for the same reason.
 *
 * ## Screen states — not `Async`
 *
 * Same reasoning `VerifyDevice` and `ResetPassword` give: this screen has no
 * data to be loading, empty or stale. It asks a question nobody has answered
 * yet — is this token still good, for this signed-in account — and the
 * failure arms are not one "something went wrong". `unreachable` (network
 * blip, 5xx, 429) is worth a retry, because the same token may simply not
 * have been reachable yet. `mismatch`, `closed` and `invalid` are not: the
 * server has already given a definitive answer about the TOKEN or the
 * ACCOUNT, and pressing the same button again repeats the identical
 * rejection.
 *
 * ## Six auth states, and only one of them calls `accept`
 *
 * `unknown` / `bootstrapping` wait rather than assume signed-out — deciding
 * either way before Firebase has reported would either POST with no session
 * or bounce a returning user to `/signin` for a frame. `mfaRequired` and
 * `signedOut` both preserve the destination through `rememberReturnTo`,
 * exactly as `RequireAuth` does for chrome routes — a token spent for
 * nothing, because the browser that opens an invite link is not reliably one
 * with a live session, is the failure this screen exists to prevent.
 * `sessionUnavailable` renders the same screen `RequireAuth` does: the
 * credential is fine, only the backend didn't answer, and there is no invite
 * data to fall back to describing the cause itself. Only `signedIn` reaches
 * `POST /invitations/{token}/accept`.
 *
 * ## M1-R8 is half-delivered, and the other half is not obtainable here
 *
 * M1-R8 (`PRD-M1-prelogin-onboarding.md:45`): *"`/invite/:token` renders for
 * an unauthenticated visitor, showing who invited them and to what."* The
 * `signedOut` arm below renders for that visitor, but it cannot name the
 * inviter or the household. `GET /invitations/{token}/open`
 * (`households.py:517-557`) is the only endpoint that carries either — and it
 * is a 302 to `expensemonitor://invite/accept?token=…&household=…&role=…`.
 * A browser `fetch` against it with `redirect: 'manual'` (the only way to
 * inspect a redirect without following it off-scheme) yields an
 * opaque-redirect response: no readable `Location`, by the Fetch spec, not by
 * a bug here. There is nothing this screen can read the names from. What
 * ships instead: an invitation-aware card naming the SITUATION rather than a
 * blind bounce, with Sign in / Create an account actions that carry the
 * destination through exactly as `mfaRequired` does.
 *
 * ## Why success navigates to `/household`, not Overview
 *
 * M1-R10 says Overview; `/household` honours its INTENT rather than its
 * letter — landing here is not setup, it's confirmation of the household this
 * invite just joined, which Overview cannot show. Settled this way in the
 * implementation plan, not re-derived here.
 */

type Screen =
  | { status: 'accepting' }
  | { status: 'unreachable' }
  | { status: 'invalid' }
  | { status: 'mismatch' }
  | { status: 'confirmJoin'; householdName: string; soleMember: boolean }
  | { status: 'blockedLastOwner' }
  | { status: 'closed'; reason: 'accepted' | 'revoked' | 'expired' }

/** The body FastAPI sends by default — `{"detail": "..."}` — read defensively
 *  rather than assumed, since the caller here is an error path and the shape
 *  is `unknown` all the way from `HouseholdError`. */
function detailOf(body: unknown): string {
  if (typeof body !== 'object' || body === null || !('detail' in body)) return ''
  const { detail } = body as { detail: unknown }
  return typeof detail === 'string' ? detail : ''
}

/** The 409 body's object `detail`, read defensively — the generated client
 *  documents no 409 for this endpoint, so nothing here is typed for us. */
function conflictOf(body: unknown): { code: string; name: string; soleMember: boolean } | null {
  if (typeof body !== 'object' || body === null || !('detail' in body)) return null
  const { detail } = body as { detail: unknown }
  if (typeof detail !== 'object' || detail === null) return null
  const d = detail as Record<string, unknown>
  // `status` is canonical; `code` carries the same value and is the fallback
  // for a backend that predates it.
  const code = typeof d.status === 'string' ? d.status : d.code
  if (typeof code !== 'string') return null
  return {
    code,
    name: typeof d.current_household_name === 'string' ? d.current_household_name : '',
    soleMember: d.sole_member === true,
  }
}

// The accept body (`InvitationAcceptOptions`) is typed by the generated client.
// The 409 shapes are undeclared in the spec, so `conflictOf` stays hand-typed.

/** `Invitation is accepted`, `Invitation is revoked`, `Invitation is expired`
 *  — `accept_invitation`'s own f-string for "not pending", one word per
 *  `InvitationStatus` member other than `pending` itself. */
const CLOSED_STATUS = /^Invitation is (accepted|revoked|expired)$/

/**
 * What a rejected accept means, from the HTTP status and the bare `detail`
 * string. Every string below was read out of
 * `expense-monitor-backend/api/src/services/invitation_service.py:accept_invitation`
 * directly, not inferred from `openapi.json` — that schema documents only 200
 * and 422 for this endpoint, and every 400 and the 404 it actually raises are
 * absent from it entirely.
 *
 * Unrecognised 4xx falls to `invalid` rather than `unreachable`: a
 * classification a future server message doesn't reach must not be treated
 * as retryable, because retrying a 4xx repeats the identical rejection. Only
 * 5xx, 429, and "no status at all" (a plain network failure, handled by the
 * caller's `catch` before this function is ever reached) are retryable.
 *
 * `"Already a member of this household"` is deliberately NOT one of the arms
 * here — see the call site in `accept()`. It isn't a failure this screen
 * needs to explain; the user already has what accepting was for.
 */
function classify(status: number, body: unknown): Screen {
  const detail = detailOf(body)
  if (status >= 500 || status === 429) return { status: 'unreachable' }
  // 409 carries an OBJECT detail whose `code` is the contract (`message` is
  // fallback prose and is never rendered). The invitation stays pending on
  // either code. An unknown code, or a string detail, is the generic failure.
  if (status === 409) {
    const conflict = conflictOf(body)
    if (conflict?.code === 'already_in_household') {
      return {
        status: 'confirmJoin',
        householdName: conflict.name,
        soleMember: conflict.soleMember,
      }
    }
    if (conflict?.code === 'last_owner_with_members') return { status: 'blockedLastOwner' }
    return { status: 'invalid' }
  }
  if (detail === 'This invitation was sent to a different email address') {
    return { status: 'mismatch' }
  }
  if (detail === 'Invitation has expired') return { status: 'closed', reason: 'expired' }
  const closed = CLOSED_STATUS.exec(detail)
  if (closed) return { status: 'closed', reason: closed[1] as 'accepted' | 'revoked' | 'expired' }
  return { status: 'invalid' }
}

export function AcceptInvitation() {
  const { token } = useParams()
  const auth = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const client = useQueryClient()
  const [screen, setScreen] = useState<Screen>({ status: 'accepting' })
  // Guards the auto-fire in the effect below against `<StrictMode>`'s double
  // invoke — `main.tsx` mounts under it, and React re-runs the SAME effect
  // closure a second time with no re-render in between, so a `useState`
  // guard reads `false` both times and the token gets POSTed twice. Measured
  // on the path this screen exists for: a signed-out visitor completes
  // sign-in and `RedirectWhenSignedIn` lands them back on `/invite/:token`
  // in the SAME document, so the screen mounts with `auth.status` already
  // `signedIn` and both StrictMode invocations see it.
  //
  // A REF, not a ref DERIVED FROM `auth` — those are different things. The
  // objection this comment used to raise was against the latter: `auth` is a
  // fresh object on every render of `AuthProvider`'s value, so a ref that
  // read `auth` would need resetting on an identity change it has no way to
  // observe. A plain boolean has neither problem — it starts `false` once,
  // survives StrictMode's simulated unmount/remount, and is set
  // synchronously, so the second invocation sees it set by the first. The
  // retry button calls `accept` directly and does not go through this guard.
  const tried = useRef(false)

  // Join's lock: set synchronously, so a second click in the same tick is a
  // no-op even before the re-render removes the button.
  const joining = useRef(false)

  async function join(tok: string) {
    if (joining.current) return
    joining.current = true
    try {
      await accept(tok, true)
    } finally {
      joining.current = false
    }
  }

  async function accept(tok: string, leaveCurrent = false) {
    setScreen({ status: 'accepting' })
    try {
      const { data, error, response } = await api.POST('/api/v1/invitations/{token}/accept', {
        params: { path: { token: tok } },
        ...(leaveCurrent ? { body: { leave_current: true } } : {}),
      })
      if (error !== undefined || data === undefined) {
        const detail = detailOf(error)
        // Not a failure: accepting is idempotent, and an account that already
        // holds this membership (the losing half of a double accept, or one
        // that joined after the invite was sent) should land where it wanted,
        // not see an error for a state that is fine. A link that was truly
        // spent returns "Invitation is accepted" instead, handled below by
        // `classify`. `/household` resolves it fresh
        // (Task 2's `useHouseholdId`) — this error body carries no
        // `household_id` to write directly, and re-deriving it from
        // `GET /households` is exactly what that hook already does.
        //
        // **Known limitation, not an oversight.** If the signed-in account
        // belongs to more than one household — the NORMAL outcome of being
        // invited, since the backend auto-creates a personal household at
        // signup and accepting never replaces it — `useHouseholdId`'s
        // member-count heuristic may not land them in the household THIS
        // invitation was for specifically. This response carries no
        // `household_id` to disambiguate with, so there is nothing here to
        // bind to instead. Issue 323 (`role` and `member_count` on the
        // invitation list item) is what eventually removes the ambiguity.
        if (detail === 'Already a member of this household') {
          // The backend reaches this check only after status, expiry and email
          // match, so it is the invitee's own account joining populated data
          // (M1-R10). "Invitation is accepted" is NOT such a proof — anyone
          // opening a spent link gets it — so only this arm marks.
          void auth.markOnboarded()
          navigate('/household', { replace: true })
          return
        }
        setScreen(classify(response.status, error))
        return
      }
      writeHouseholdId(data.household_id)
      // The list `useHouseholdId` (Task 2) resolves against is 30s-stale
      // (`QueryProvider.tsx`'s default) and this membership is brand new —
      // harmless today, since nothing links to this route from inside the
      // app, but a cached list missing it would make that hook's
      // single-household shortcut bind to the wrong one if that ever
      // changes.
      //
      // A Join also LEAVES the old household: sharing grants are revoked both
      // ways and shared merchants go back to personal, so any cached query may
      // now be wrong. Same broad invalidate `useLeaveHousehold` uses, for the
      // same reason — it cannot go stale as modules gain relationships.
      void client.invalidateQueries(leaveCurrent ? undefined : { queryKey: keys.household.list() })
      // M1-R10: they are joining populated data, so first run must not follow.
      // The backend's accept leaves `is_onboarded` alone, and `RequireAuth`
      // sends a new account to /welcome — so record it here, only on a
      // genuine accept (every rejection above returns first). Not awaited:
      // `markOnboarded` flips the profile synchronously and a failed PATCH
      // costs one more first run, never a stuck screen.
      void auth.markOnboarded()
      navigate('/household', { replace: true })
    } catch {
      // `fetch` rejects for a network failure or a refused CORS preflight —
      // no status, so `classify` is never reached. The token may still be
      // good, which is what makes this the retryable arm.
      setScreen({ status: 'unreachable' })
    }
  }

  useEffect(() => {
    if (tried.current) return
    if (auth.status !== 'signedIn') return
    if (token === undefined || token === '') return
    tried.current = true
    void accept(token)
    // `accept` closes over `navigate`, `setScreen` and `client`, all stable
    // enough for this effect's purpose; re-running it on their identity is
    // not what `tried` exists to prevent, so they are deliberately left out.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.status, token])

  if (auth.status === 'unknown' || auth.status === 'bootstrapping') {
    return (
      <AuthCard title="One moment">
        <p className="auth-card__note" role="status">
          Checking your session…
        </p>
      </AuthCard>
    )
  }

  if (auth.status === 'mfaRequired') {
    // Same mechanism `RequireAuth` uses for chrome routes: written before the
    // redirect renders, so the token survives whatever the challenge screen
    // does next.
    rememberReturnTo(`${location.pathname}${location.search}`)
    return <Navigate to={MFA_PATH} replace />
  }

  if (auth.status === 'sessionUnavailable') {
    // The credential is fine, only the backend didn't answer — the same
    // situation `RequireAuth` renders this screen for, and there is no
    // invite-specific detail to add to it.
    return <SessionUnavailable />
  }

  if (auth.status === 'signedOut') {
    // `rememberReturnTo` is unchanged — a link spent for nothing is the
    // failure this screen exists to prevent, and it has to be written before
    // whatever the visitor does next. Only the RENDER changed: this used to
    // bounce straight to `/signin` with nothing said. See this file's
    // "M1-R8 is half-delivered" note above for why the inviter and household
    // name can't be shown — this card names the SITUATION instead.
    rememberReturnTo(`${location.pathname}${location.search}`)
    return (
      <AuthCard title="You've been invited to a TrackE5 household">
        <p className="auth-card__note">
          Sign in or create an account to accept the invitation.
        </p>
        <Button variant="primary" size="page" onClick={() => navigate('/signin')}>
          Sign in
        </Button>
        <Button variant="outline" size="page" onClick={() => navigate('/signup')}>
          Create an account
        </Button>
      </AuthCard>
    )
  }

  // `auth.status === 'signedIn'` from here — every other arm of `AuthState`
  // has already returned.

  // Decided at render, like `VerifyDevice`'s missing-token case: `useParams`
  // types `token` as possibly undefined, and the effect above never fires
  // `accept` for one, so `screen` would otherwise sit at `accepting` forever
  // with nothing to show for it.
  const resolved: Screen = token === undefined || token === '' ? { status: 'invalid' } : screen

  if (resolved.status === 'accepting') {
    return (
      <AuthCard title="Joining your household">
        <p className="auth-card__note" role="status">
          Accepting your invite…
        </p>
      </AuthCard>
    )
  }

  if (resolved.status === 'unreachable') {
    return (
      <AuthCard title="Joining your household">
        <p className="auth-card__note" role="alert">
          We couldn't reach TrackE5 to accept that invite. Your link is still valid, so check your
          connection and try again.
        </p>
        <Button
          variant="primary"
          size="page"
          onClick={() => {
            if (token !== undefined && token !== '') void accept(token)
          }}
        >
          Try again
        </Button>
      </AuthCard>
    )
  }

  if (resolved.status === 'mismatch') {
    // Real and likely — people have more than one address. No retry: the
    // server has already given a definitive answer about the ACCOUNT, and
    // repeating the same request from the same account repeats the same
    // rejection.
    //
    // The invitation's own target address is not something this screen has
    // — verified against `invitation_service.py`: `invitation.email` is
    // known server-side and never returned in this rejection's body, bare
    // `{"detail": "..."}` with no email field. Naming the SIGNED-IN address
    // instead isn't a fallback for missing data, it's the more useful fact
    // regardless: knowing the invited address doesn't help if the person
    // can't see which one they're currently using, and "which account am I
    // on" is what actually tells them what to do next.
    return (
      <AuthCard title="This invitation was sent to a different email address.">
        <p className="auth-card__note" role="alert">
          {auth.user.email !== null
            ? `You are signed in as ${auth.user.email}. `
            : "You aren't signed in with an email address. "}
          Sign in with the address the invitation was sent to.
        </p>
      </AuthCard>
    )
  }

  if (resolved.status === 'confirmJoin') {
    // Nothing was accepted, so no `markOnboarded` here — only the Join call's
    // own 200 reaches that path. The invitation is still pending. The name is
    // interpolated into a text node, never markup.
    const old = resolved.householdName !== '' ? resolved.householdName : 'your current household'
    return (
      <AuthCard title="Join this household?">
        <p className="auth-card__note" role="alert">
          {resolved.soleMember
            ? `You're already in ${old}, and you're the only member, so ${old} will close if you join.`
            : `You're already in ${old}. If you join, sharing in ${old} ends both ways: you stop seeing what others there shared with you, and they stop seeing what you shared.`}{' '}
          Merchants you shared there become personal again. Your bank accounts stay yours and stay
          connected, and nothing is shared into the new household automatically.
        </p>
        <Button
          variant="primary"
          size="page"
          onClick={() => {
            if (token !== undefined && token !== '') void join(token)
          }}
        >
          Join
        </Button>
        <ButtonLink to={OVERVIEW_PATH} variant="outline" size="page">
          Go to the app
        </ButtonLink>
      </AuthCard>
    )
  }

  if (resolved.status === 'blockedLastOwner') {
    return (
      <AuthCard title="You can't leave your household yet">
        <p className="auth-card__note" role="alert">
          You're the only owner of a household that still has other members. Make another member
          an owner first, then open this invitation link again.
        </p>
        <ButtonLink to="/household" variant="primary" size="page">
          Go to household
        </ButtonLink>
      </AuthCard>
    )
  }

  if (resolved.status === 'closed') {
    const reasonCopy: Record<'accepted' | 'revoked' | 'expired', string> = {
      accepted: 'This invitation has already been accepted.',
      revoked: 'This invitation has been revoked.',
      expired: 'This invitation has expired.',
    }
    return (
      <AuthCard title="This invite is no longer available">
        {/* No retry — none of the three can be made to work by trying again.
            Only a household admin can send a fresh one, which is outside
            what this screen can do. */}
        <p className="auth-card__note" role="alert">
          {reasonCopy[resolved.reason]}
        </p>
      </AuthCard>
    )
  }

  // resolved.status === 'invalid' — a bad or unknown token, or a rejection
  // this screen doesn't have specific copy for. Treated as the same dead end
  // rather than guessing at a cause the server didn't give.
  return (
    <AuthCard title="This invite link isn't valid">
      <p className="auth-card__note" role="alert">
        We couldn't find that invitation. It may have been mistyped, or it may no longer exist.
      </p>
    </AuthCard>
  )
}
